import { createProductListCategoryGroups } from '@features/products/product-list-build.helpers';
import { getConversionFactorValue } from '@shared/helpers/conversion-factors.helpers';
import { getElementCategory } from '@shared/helpers/element-category.helpers';
import { getEPDProductId } from '@shared/helpers/product-element.helpers';
import { getResultsByFactor } from '@shared/helpers/results.helpers';
import {
  getVersionContentCoveragePercent,
  scaleResultsRecordByVersionContentCoverage,
} from '@shared/helpers/results/content-coverage.helpers';
import type { SbefScopeGroupKey } from '@shared/construction/sbef-custom-groups.data';
import type { IProductListCategoryGroup } from '@shared/models/product.model';
import type { Baseline } from '@shared/models/project.model';
import type { ResultsRecord } from '@shared/models/unit.model';

import type {
  ProductGroupSegment,
  ProductGroupsChartData,
  ProductGroupsDonutCenter,
} from './report-product-groups.model';

/**
 * Center label for a non-zero rounded delta, or {@link ProductGroupsDonutCenter}
 * `none` when the scenario matches baseline (including the baseline page).
 */
function buildDonutCenter(reductionPct: number): ProductGroupsDonutCenter {
  const rounded = Math.round(reductionPct);
  if (rounded > 0) return { kind: 'delta', text: `${rounded}% reduction` };
  if (rounded < 0)
    return { kind: 'delta', text: `${Math.abs(rounded)}% increase` };
  return { kind: 'none' };
}

type CategoryCo2eByCategoryId = Record<string, number>;

export type BuildReportProductGroupsChartDataOptions = {
  baselineVersion: Baseline;
  scenarioVersion: Baseline;
  baselineRecord: ResultsRecord;
  scenarioRecord: ResultsRecord;
  selectedSbefGroups: readonly SbefScopeGroupKey[];
  gfa: number;
  isBaselineScenario: boolean;
  accentColor: string;
  /** Build and the summary slide total, kg CO₂e per m². */
  baselineRollupCo2ePerGfa: number;
  /** Same rollup for the scenario. See {@link baselineRollupCo2ePerGfa}. */
  scenarioRollupCo2ePerGfa: number;
};

/**
 * Build a `{categoryId: co2ePerGfa}` lookup for a version + filtered results record.
 */
const buildCategoryCo2eRecord = (
  version: Baseline,
  resultsRecord: ResultsRecord,
  gfa: number,
  selectedSbefGroups: readonly SbefScopeGroupKey[],
): {
  record: CategoryCo2eByCategoryId;
  groups: IProductListCategoryGroup[];
} => {
  const groups = createProductListCategoryGroups(
    version,
    resultsRecord,
    selectedSbefGroups,
  );
  const record: CategoryCo2eByCategoryId = {};
  for (const group of groups) {
    record[group.id] = getResultsByFactor(
      getConversionFactorValue(group.results, 'co2e'),
      gfa,
    );
  }
  return { record, groups };
};

export type ComputeScenarioEpdCoveragePercentOptions = {
  scenarioVersion: Baseline;
  scenarioRecord: ResultsRecord;
  selectedSbefGroups: readonly SbefScopeGroupKey[];
};

/**
 * Share of scenario product CO₂e from elements with an applied EPD (0–100).
 * Uses the same SBEF scope as {@link createProductListCategoryGroups}; callers
 * should pass lifecycle-filtered flat result rows.
 */
export const computeScenarioEpdCoveragePercent = ({
  scenarioVersion,
  scenarioRecord,
  selectedSbefGroups,
}: ComputeScenarioEpdCoveragePercentOptions): number => {
  const groups = createProductListCategoryGroups(
    scenarioVersion,
    scenarioRecord,
    selectedSbefGroups,
  );

  let totalCo2e = 0;
  let epdCo2e = 0;

  for (const categoryGroup of groups) {
    for (const productGroup of categoryGroup.elements) {
      for (const item of productGroup.elements) {
        const co2e = getConversionFactorValue(item.results, 'co2e');
        if (co2e <= 0) continue;
        totalCo2e += co2e;
        if (getEPDProductId(item)) epdCo2e += co2e;
      }
    }
  }

  if (totalCo2e <= 0) return 0;
  return (epdCo2e / totalCo2e) * 100;
};

/**
 * kg CO₂e per m² from the version rollup row Build and the summary slide use.
 * Product rows in the same record are ignored.
 *
 * @param record - Proposal or baseline results, keyed by element id
 * @param versionId - Version element id, where the rollup total is stored
 * @param gfa - Project gross floor area
 */
export const versionRollupCo2ePerGfa = (
  record: ResultsRecord | undefined,
  versionId: string,
  gfa: number,
): number =>
  getConversionFactorValue(
    getResultsByFactor(record?.[versionId] ?? {}, gfa),
    'co2e',
  );

/**
 * Pure builder for {@link ProductGroupsChartData} used by the report product-groups
 * donut. SBEF scope is applied via {@link createProductListCategoryGroups}; callers
 * should pass lifecycle-filtered flat result rows (see
 * {@link filterProductGroupChartResultsRecord}). Category rows are scaled
 * for version Content coverage. The detail KPI uses
 * {@link BuildReportProductGroupsChartDataOptions.scenarioRollupCo2ePerGfa},
 * which already includes coverage.
 */
export const buildReportProductGroupsChartData = (
  options: BuildReportProductGroupsChartDataOptions,
): ProductGroupsChartData | undefined => {
  const {
    baselineVersion,
    scenarioVersion,
    baselineRecord,
    scenarioRecord,
    selectedSbefGroups,
    gfa,
    isBaselineScenario,
    accentColor,
    baselineRollupCo2ePerGfa,
    scenarioRollupCo2ePerGfa,
  } = options;

  // Product rows omit version Content coverage (applied only on the version
  // rollup). Scale the donut rows here. The KPI uses the rollup directly.
  const coveredBaselineRecord = scaleResultsRecordByVersionContentCoverage(
    baselineRecord,
    baselineVersion,
  );
  const coveredScenarioRecord = scaleResultsRecordByVersionContentCoverage(
    scenarioRecord,
    scenarioVersion,
  );

  const baseline = buildCategoryCo2eRecord(
    baselineVersion,
    coveredBaselineRecord,
    gfa,
    selectedSbefGroups,
  );
  const scenario = buildCategoryCo2eRecord(
    scenarioVersion,
    coveredScenarioRecord,
    gfa,
    selectedSbefGroups,
  );

  const baselineCategoryTotal = Object.values(baseline.record).reduce(
    (sum, value) => sum + value,
    0,
  );
  const scenarioCategoryTotal = Object.values(scenario.record).reduce(
    (sum, value) => sum + value,
    0,
  );
  // The headline is the Build rollup. Category rows stay on the donut.
  if (baselineCategoryTotal <= 0 && scenarioCategoryTotal <= 0)
    return undefined;

  const unsortedSegments = baseline.groups
    .map((group): ProductGroupSegment | undefined => {
      const baselineCo2e = baseline.record[group.id] ?? 0;
      if (baselineCo2e <= 0) return undefined;
      const scenarioCo2e = scenario.record[group.id] ?? 0;
      const categoryName =
        getElementCategory(group.id)?.name ?? String(group.id);
      const arrow =
        isBaselineScenario || baselineCo2e === scenarioCo2e
          ? undefined
          : baselineCo2e - scenarioCo2e;
      return {
        id: group.id,
        label: categoryName,
        value: baselineCo2e,
        cloudValue: scenarioCo2e,
        ...(arrow != null && arrow !== 0 ? { arrowValue: arrow } : {}),
        color: '#000000',
      };
    })
    .filter((segment): segment is ProductGroupSegment => segment !== undefined);

  if (unsortedSegments.length === 0) return undefined;

  const segments = [...unsortedSegments]
    .sort((a, b) => b.value - a.value)
    .map((segment) => ({
      ...segment,
      color: accentColor,
    }));

  const proposalSummary = {
    proposalCo2ePerGfa: isBaselineScenario
      ? baselineRollupCo2ePerGfa
      : scenarioRollupCo2ePerGfa,
    offsetCo2ePerGfa: isBaselineScenario
      ? 0
      : scenarioRollupCo2ePerGfa - baselineRollupCo2ePerGfa,
  };

  const epdCoveragePercent = computeScenarioEpdCoveragePercent({
    scenarioVersion,
    scenarioRecord: coveredScenarioRecord,
    selectedSbefGroups,
  });
  const contentCoveragePercent =
    getVersionContentCoveragePercent(scenarioVersion);

  if (isBaselineScenario) {
    return {
      center: buildDonutCenter(0),
      proposalSummary,
      segments,
      epdCoveragePercent,
      contentCoveragePercent,
    };
  }

  const reductionPerGfa = baselineRollupCo2ePerGfa - scenarioRollupCo2ePerGfa;
  const reductionPct =
    baselineRollupCo2ePerGfa > 0
      ? (reductionPerGfa / baselineRollupCo2ePerGfa) * 100
      : 0;

  return {
    center: buildDonutCenter(reductionPct),
    proposalSummary,
    segments,
    epdCoveragePercent,
    contentCoveragePercent,
  };
};
